import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { useAuth } from '../../lib/auth-context';
import { OfficialBadge, CommunityBadge, VerifiedBadge } from '../../components/common/Badges';
import { MapView } from '../../components/common/MapView';
import { Button } from '../../components/ui/button';
import { ArrowLeft, MapPin, Calendar, Users, Award, ChevronRight, Loader2, Clock, AlertTriangle, Play, Square, Radio, CheckCircle2, Trash2 } from 'lucide-react';
import { formatEventDate, formatEventTime, formatDuration } from '../../lib/formatters';
import { formatFee } from '../../lib/currency';
import { useUnits, formatEventDistance } from '../../lib/units';
import { ShareEventButton } from '../../components/common/ShareEventButton';
import { VirtualCountdownCard } from '../../components/common/VirtualCountdownCard';
import { toast } from 'sonner';

export default function MobileEventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { units } = useUnits();
  const { user } = useAuth();
  const { data: event, loading, refresh: refreshEvent } = useApi(api.getEvent, [id]);
  const { data: leaderboard } = useApi(api.getLeaderboard, [id]);
  const { data: myParticipation, loading: myPartLoading } = useApi(api.getMyParticipation, [id]);
  const { data: participantsList, refresh: refreshParticipants } = useApi(api.getEventParticipants, [id]);
  const [busy, setBusy] = useState(false);

  // If the race is LIVE and the user is a participant in a running/ready state,
  // auto-redirect them to the live race screen so a shared link → open →
  // takes them straight into the tracking view instead of the marketing page.
  // Note: virtual joined-but-not-checked-in participants (`state='joined'`)
  // must also be redirected since virtual runs skip the start-line check-in.
  const shouldEnterRace =
    event?.raceState === 'live' &&
    myParticipation &&
    ['joined', 'ready', 'checked-in', 'live'].includes(myParticipation.state) &&
    event.organizer_id !== user?.id;
  useEffect(() => {
    if (shouldEnterRace) {
      nav(`/events/${id}/race`, { replace: true });
    }
  }, [shouldEnterRace, id, nav]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (!event) {
    return (
      <div className="p-6">
        <p>Event not found.</p>
        <Button variant="ghost" onClick={() => nav(-1)}>Go back</Button>
      </div>
    );
  }

  const isOrganizer = user?.id && event.organizer_id === user.id;
  const isLive = event.raceState === 'live';
  const isCompleted = event.raceState === 'completed';
  const isUpcoming = !isLive && !isCompleted;

  async function handleStart() {
    setBusy(true);
    try {
      await api.startRace(event.id);
      toast.success('Race started — runners can begin');
      refreshEvent();
      refreshParticipants?.();
    } catch (err) {
      toast.error(err.message || 'Could not start race');
    } finally { setBusy(false); }
  }
  async function handleEnd() {
    if (!window.confirm('End the race? Anyone still running will be auto-finished.')) return;
    setBusy(true);
    try {
      await api.endRace(event.id);
      toast.success('Race ended');
      refreshEvent();
      refreshParticipants?.();
    } catch (err) {
      toast.error(err.message || 'Could not end race');
    } finally { setBusy(false); }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete "${event.name}"? This cannot be undone — all participations will be removed.`)) return;
    setBusy(true);
    try {
      await api.deleteEvent(event.id);
      toast.success('Event deleted');
      nav('/events', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Could not delete event');
      setBusy(false);
    }
  }

  return (
    <div className="pb-6" data-testid="event-detail">
      <div className="h-56 bg-cover bg-center relative" style={{ backgroundImage: `url(${event.image})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <button onClick={() => nav(-1)} data-testid="event-detail-back" className="absolute top-3 left-3 h-9 w-9 rounded-md bg-background/80 backdrop-blur flex items-center justify-center">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {isLive && (
            <span
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-success text-white text-[10px] font-bold uppercase tracking-widest"
              data-testid="event-live-badge"
            >
              <Radio className="h-3 w-3 animate-pulse" /> Live
            </span>
          )}
          {event.type === 'official' ? <OfficialBadge /> : <CommunityBadge />}
        </div>
      </div>

      <div className="px-4 -mt-6 relative z-10">
        <div className="rivl-card bg-card p-4">
          <h1 className="font-display text-2xl font-bold tracking-tight leading-tight">{event.name}</h1>
          <p className="mt-1 text-xs text-muted-foreground">by {event.organizer || '—'}</p>

          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <Stat label="Distance" value={formatEventDistance(event.distanceKm, units)} />
            <Stat label="Joined" value={`${event.participants}`} />
            <Stat label="Capacity" value={`${event.capacity}`} />
          </div>

          <div className="mt-4 space-y-2 text-sm">
            <Row icon={Calendar} text={`${formatEventDate(event.date)} · ${formatEventTime(event.date)}`} />
            {event.eventMode === 'virtual' ? (
              <Row icon={MapPin} text="Virtual · run anywhere" />
            ) : (
              event.location && <Row icon={MapPin} text={event.location} />
            )}
            {event.joinDeadline && (
              <DeadlineRow deadline={event.joinDeadline} />
            )}
            {event.medalEnabled && <Row icon={Award} text="Verified medal collection" />}
            {event.bibMode && event.bibMode !== 'none' && <Row icon={Users} text={`Bib mode: ${event.bibMode}`} />}
          </div>

          {/* Action row — depends on race state and whether user is the organizer */}
          {isOrganizer ? (
            <div className="mt-4 grid grid-cols-2 gap-2" data-testid="organizer-controls">
              {isUpcoming && (
                <Button
                  onClick={handleStart}
                  disabled={busy}
                  className="h-11 rounded-md bg-success hover:bg-success/90 text-white"
                  data-testid="organizer-start-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-2" /> Start race</>}
                </Button>
              )}
              {isLive && (
                <Button
                  onClick={handleEnd}
                  disabled={busy}
                  className="h-11 rounded-md bg-destructive hover:bg-destructive/90 text-white"
                  data-testid="organizer-end-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Square className="h-4 w-4 mr-2" /> End race</>}
                </Button>
              )}
              {isCompleted && (
                <div
                  className="col-span-2 rivl-card p-3 text-center text-xs text-muted-foreground"
                  data-testid="organizer-completed"
                >
                  Race complete.
                </div>
              )}
              <ShareEventButton event={event} className="h-11 rounded-md" data-testid="organizer-share-btn" />
              {/* Organizer can also participate in their own race. */}
              {!isCompleted && !myParticipation && (
                <Button
                  asChild
                  variant="outline"
                  className="col-span-2 h-11 rounded-md"
                  data-testid="organizer-join-btn"
                >
                  <Link to={`/events/${event.id}/join`}>
                    Join as participant <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              )}
              {myParticipation && !isCompleted && (
                <div
                  className="col-span-2 h-11 rounded-md border border-success bg-success/10 text-success flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-widest"
                  data-testid="organizer-participating"
                >
                  <CheckCircle2 className="h-4 w-4" /> You&apos;re participating
                </div>
              )}
              {/* Watch-live: organizer can also spectate their live race. */}
              {isLive && (
                <Button
                  asChild
                  className="col-span-2 h-11 rounded-md bg-success/90 hover:bg-success text-white"
                  data-testid="organizer-watch-live"
                >
                  <Link to={`/events/${event.id}/leaderboard`}>
                    <Radio className="h-4 w-4 mr-1 animate-pulse" /> Watch live leaderboard
                  </Link>
                </Button>
              )}
              {!isLive && (
                <Button
                  onClick={handleDelete}
                  disabled={busy}
                  variant="outline"
                  className="col-span-2 h-11 rounded-md border-destructive/40 text-destructive hover:bg-destructive/5"
                  data-testid="organizer-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete event
                </Button>
              )}
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-3 gap-2">
              {isLive ? (
                <Button asChild className="col-span-2 h-11 rounded-md bg-success hover:bg-success/90 text-white" data-testid="event-watch-live">
                  <Link to={`/events/${event.id}/leaderboard`}>
                    <Radio className="h-4 w-4 mr-1 animate-pulse" /> Watch live <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              ) : isCompleted ? (
                <Button asChild variant="outline" className="col-span-2 h-11 rounded-md" data-testid="event-final-results">
                  <Link to={`/events/${event.id}/leaderboard`}>
                    View results <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              ) : myPartLoading ? (
                <div className="col-span-2 h-11 rounded-md border border-border flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : myParticipation ? (
                event.eventMode === 'virtual' ? (
                  <VirtualCountdownCard
                    event={event}
                    myParticipation={myParticipation}
                    active
                    onWaitingTick={refreshEvent}
                  />
                ) : (
                  <div
                    className="col-span-2 h-11 rounded-md border border-success bg-success/10 text-success flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-widest"
                    data-testid="event-already-joined"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {event.bibMode && event.bibMode !== 'none' && myParticipation.bibNumber
                      ? `You're in · Bib ${myParticipation.bibNumber}`
                      : "You're in"}
                  </div>
                )
              ) : (
                <Button asChild className="col-span-2 h-11 rounded-md" data-testid="event-join-btn">
                  <Link to={`/events/${event.id}/join`}>
                    {event.fee > 0 ? `Join · ${formatFee(event.fee, event.currency)}` : 'Join Event'} <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              )}
              <ShareEventButton event={event} className="h-11 rounded-md" />
            </div>
          )}
        </div>
      </div>

      <div className="px-4 mt-6">
        <h2 className="rivl-label mb-2">About</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">{event.description}</p>
      </div>

      {event.eventMode !== 'virtual' && (
        <div className="px-4 mt-6">
          <h2 className="rivl-label mb-2">Route preview</h2>
          <MapView
            height={180}
            startPoint={event.startLat != null && event.startLng != null ? { lat: event.startLat, lng: event.startLng } : null}
            finishPoint={event.finishLat != null && event.finishLng != null ? { lat: event.finishLat, lng: event.finishLng } : null}
          />
        </div>
      )}

      {isOrganizer && (
        <div className="px-4 mt-6" data-testid="organizer-participants-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="rivl-label">Participants · {participantsList?.length || 0}</h2>
          </div>
          {participantsList && participantsList.length > 0 ? (
            <div className="rivl-card divide-y divide-border" data-testid="organizer-participants-list">
              {participantsList.map((p) => (
                <div key={p.userId} className="flex items-center gap-3 px-4 py-3" data-testid={`participant-${p.userId}`}>
                  {p.avatar ? (
                    <img src={p.avatar} alt={p.name} className="h-9 w-9 rounded-full object-cover" />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold uppercase">
                      {(p.name || '?').charAt(0)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {p.handle && <span className="mr-2">{p.handle}</span>}
                      {p.bib && event.bibMode !== 'none' && <span className="mr-2">Bib {p.bib}</span>}
                      <span className="uppercase tracking-widest text-[9px]">{p.state.replace('-', ' ')}</span>
                    </div>
                  </div>
                  {p.finishTimeSec && (
                    <div className="font-mono text-xs text-muted-foreground">
                      {formatDuration(p.finishTimeSec)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground py-4 text-center rivl-card">
              No one has joined yet. Share the event link to get runners in.
            </p>
          )}
        </div>
      )}

      <div className="px-4 mt-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="rivl-label">Top finishers</h2>
          <Link to="/leaderboard" className="text-xs text-primary">View all →</Link>
        </div>
        {leaderboard?.length > 0 ? (
          <div className="rivl-card divide-y divide-border">
            {leaderboard.slice(0, 3).map((row) => (
              <div key={row.rank} className="flex items-center gap-3 px-4 py-3">
                <div className="font-mono font-bold w-6 text-primary">#{row.rank}</div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{row.name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">Bib {row.bib} · {row.pace}/km</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold">{row.time}</div>
                  {row.verified && <VerifiedBadge />}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground py-4 text-center">No verified finishers yet.</p>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md bg-secondary py-2">
      <div className="font-mono font-bold text-lg">{value}</div>
      <div className="rivl-label text-[9px]">{label}</div>
    </div>
  );
}
function Row({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-foreground/80">
      <Icon className="h-4 w-4 text-muted-foreground" /> {text}
    </div>
  );
}

function DeadlineRow({ deadline }) {
  const ts = new Date(deadline).getTime();
  const passed = ts < Date.now();
  return (
    <div className={`flex items-center gap-2 ${passed ? 'text-destructive' : 'text-foreground/80'}`} data-testid="event-deadline-row">
      {passed ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4 text-muted-foreground" />}
      {passed
        ? `Registration closed (${formatEventDate(deadline)})`
        : `Register by ${formatEventDate(deadline)} · ${formatEventTime(deadline)}`}
    </div>
  );
}
